import { useState } from 'react'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, TextField, Chip
} from '@mui/material'
import { DCOrderItem } from '../../types/dc.types'

interface OrderItemsTableProps {
  items: DCOrderItem[]
  editable?: boolean
  onUpdateItem?: (itemId: number, data: { order_qty?: number }) => void
  filterDistributorId?: number | null
}

export default function OrderItemsTable({
  items, editable = false, onUpdateItem, filterDistributorId
}: OrderItemsTableProps) {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editQty, setEditQty] = useState('')

  const filteredItems = filterDistributorId
    ? items.filter(i => i.distributor_id === filterDistributorId)
    : items

  const handleQtySubmit = (itemId: number) => {
    if (onUpdateItem && editQty) {
      onUpdateItem(itemId, { order_qty: parseInt(editQty) })
    }
    setEditingId(null)
    setEditQty('')
  }

  const totalCost = filteredItems.reduce((sum, i) => sum + (i.estimated_line_total || 0), 0)

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Medicine</TableCell>
              <TableCell>Pack Size</TableCell>
              {!filterDistributorId && <TableCell>Distributor</TableCell>}
              <TableCell align="right">PTR</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell>Offer</TableCell>
              <TableCell align="right">Landing</TableCell>
              <TableCell align="right">14d Demand</TableCell>
              <TableCell align="right">Order Qty</TableCell>
              <TableCell align="right">Est. Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={filterDistributorId ? 9 : 10} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No items</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {item.medicine_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.manufacturer_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.pack_size}</TableCell>
                  {!filterDistributorId && (
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 130 }}>
                        {item.distributor_name || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {item.ptr ? `${item.ptr.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {item.discount_percentage ? `${item.discount_percentage}%` : '-'}
                  </TableCell>
                  <TableCell>
                    {item.buy_qty > 0 && item.free_qty > 0 ? (
                      <Chip
                        label={`${item.buy_qty}+${item.free_qty}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {item.offer_landing_cost
                      ? `${item.offer_landing_cost.toFixed(2)}`
                      : item.landing_cost
                        ? `${item.landing_cost.toFixed(2)}`
                        : '-'}
                  </TableCell>
                  <TableCell align="right">{item.demand_14d?.toFixed(1)}</TableCell>
                  <TableCell align="right">
                    {editable && editingId === item.id ? (
                      <TextField
                        size="small"
                        value={editQty}
                        onChange={(e) => setEditQty(e.target.value)}
                        onBlur={() => handleQtySubmit(item.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleQtySubmit(item.id)}
                        autoFocus
                        sx={{ width: 70 }}
                        type="number"
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        onClick={() => {
                          if (editable) {
                            setEditingId(item.id)
                            setEditQty(String(item.order_qty))
                          }
                        }}
                        sx={editable ? { cursor: 'pointer', '&:hover': { color: 'primary.main' } } : {}}
                      >
                        {item.order_qty}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      Rs. {(item.estimated_line_total || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
            {filteredItems.length > 0 && (
              <TableRow>
                <TableCell colSpan={filterDistributorId ? 8 : 9} align="right">
                  <Typography fontWeight={600}>Total</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    Rs. {totalCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
